import { SalesforceSyncLog } from "../schema/salesforce";

/**
 * Status → semantic colour tokens. **Semantic tokens only** (apps/web/CLAUDE.md §7): the
 * Dynamics tab originally used raw palette classes (`bg-red-100 text-red-800`, …) and
 * `tailwind.config.js` defines none of those shades, so every badge except SUCCESS rendered
 * with no background at all — a FAILED row looked like plain text.
 */
export const SALESFORCE_SYNC_STATUS_COLORS: Record<string, string> = {
	SUCCESS: "bg-status-success-light text-status-success",
	PARTIAL: "bg-status-warning-light text-status-wrong-person",
	FAILED: "bg-status-error-light text-status-error",
	PENDING: "bg-status-info-light text-status-info",
	IN_PROGRESS: "bg-status-running-light text-status-running",
};

export const statusColorFor = (status: string): string =>
	SALESFORCE_SYNC_STATUS_COLORS[status] ?? "bg-gray-100 text-gray-800";

/**
 * The first error message of a log row.
 *
 * `SalesforceSyncLogDTO.errors` is an array of free-form objects — there is no flat
 * `errorMessage` column. Every writer stores `{ prospectId, error }`, so `error` is the key
 * that matters; reading `message` instead is what made the Dynamics tab show "—" for every
 * failure a customer could have acted on. `message` stays as a fallback because the column is
 * typed `Record<string, unknown>` and nothing stops a future writer from using it.
 */
export const firstErrorMessage = (log: SalesforceSyncLog): string | null => {
	const entry = log.errors?.[0];
	const text = entry?.error ?? entry?.message;

	return typeof text === "string" && text.length > 0 ? text : null;
};

/**
 * What the Error column shows: Salesforce's own machine-readable code and the fields it named,
 * ahead of the free-text message.
 *
 * The code is the actionable half — `DUPLICATES_DETECTED` sends the customer to their duplicate
 * rules, `REQUIRED_FIELD_MISSING` to their mappings — and it is the one part of a rejection that
 * is stable enough to search for. The message alone frequently reads as an opaque API string.
 */
export const describeSyncLogError = (log: SalesforceSyncLog): string => {
	const parts: string[] = [];

	if (log.errorCode) {
		parts.push(log.errorCode);
	}

	if (log.errorFields?.length) {
		parts.push(`(${log.errorFields.join(", ")})`);
	}

	const message = firstErrorMessage(log);

	if (message) {
		parts.push(message);
	}

	return parts.length > 0 ? parts.join(" ") : "—";
};

/**
 * Whether the row was rejected by the org's own duplicate rules, which is a customer
 * configuration matter rather than a mapping problem and gets its own hint.
 *
 * Both codes count: `DUPLICATES_DETECTED` comes from a duplicate *rule*, `DUPLICATE_VALUE` from
 * a unique field. Salesforce uses them interchangeably enough that showing the hint for only
 * one of them would look arbitrary — the backend's `DUPLICATE_ERROR_CODES` treats them the same.
 */
export const isDuplicateRejection = (log: SalesforceSyncLog): boolean =>
	log.errorCode === "DUPLICATES_DETECTED" || log.errorCode === "DUPLICATE_VALUE";
